import { useContext } from 'react';
import FilterEvents from '~/constants/events';
import SearchSource from '~/constants/SearchSource';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import { useTrackEvent } from '~/contexts/SearchAnalyticsContext';
import SearchEventsDataContext from '~/contexts/SearchEventsDataContext';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import {
  Input,
  InputsWrapper,
  Range,
  RangeHeader,
  RangeLabels,
  RangeWrapper,
  Wrapper
} from './FacetFinanceRange.styles';

export type FacetValue = 'downPayment' | 'monthlyPayment';

export type InputType = 'input' | 'slider';

type OwnProps = {
  debounceDelay?: number;
  facetValue: FacetValue;
  handleSliderBlur?: () => void;
  handleSliderFocus?: () => void;
  hasIncline?: boolean;
  inputId?: string;
  inputValue?: number;
  inputWidth?: number;
  isMobile?: boolean;
  maxRange?: number;
  minRange?: number;
  onAfterChange: () => void;
  onChange: (
    facetValue: FacetValue,
    rangeValue: number,
    eventType: InputType
  ) => void;
  onInputChange: (facetValue: FacetValue, value: number) => void;
  sliderValue?: number;
  step?: number;
  title?: string;
  withLessThan?: boolean;
};

export default function FacetRange({
  debounceDelay,
  facetValue,
  handleSliderBlur,
  handleSliderFocus,
  hasIncline = false,
  inputId,
  inputValue,
  inputWidth,
  isMobile,
  maxRange = 0,
  minRange = 0,
  onAfterChange,
  onChange,
  onInputChange,
  sliderValue,
  step,
  title = '',
  withLessThan = false
}: OwnProps) {
  const {
    appliedFilters: { finance }
  } = useContext(AppliedFiltersContext);
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const trackEvent = useTrackEvent();
  const { monthlyPayment, downPayment } = finance ?? {};

  const isDownPaymentFilter = facetValue === 'downPayment';
  const eventName = isDownPaymentFilter
    ? FilterEvents.cashDown
    : FilterEvents.monthlyPayment;
  const previousValue = isDownPaymentFilter ? downPayment : monthlyPayment;

  return (
    <Wrapper>
      <RangeHeader hasIncline={hasIncline}>
        <span>{title}</span>
        <InputsWrapper>
          {withLessThan && '< '}
          <Input
            dataQa={`${facetValue}-range-input`}
            delay={debounceDelay}
            focusEventType={facetValue}
            id={inputId}
            maxLength={6}
            minLength={1}
            onChange={evt => {
              const nextValue = parseInt(evt.target.value.replace(/\D/g), 10);
              onInputChange(facetValue, nextValue);
              setSearchSource(SearchSource.Facet);
              trackEvent(eventName, {
                previousValue,
                nextValue
              });
            }}
            pattern="\d*"
            triggerOnTextChange={false}
            type="number"
            value={inputValue}
            width={inputWidth}
            withDollarSign
          />
        </InputsWrapper>
      </RangeHeader>
      <RangeWrapper title={title} isMobile={isMobile}>
        <Range
          handleStyle={{
            borderColor: 'var(--cvna-color-blue-2)',
            height: 25,
            width: 25,
            marginTop: -12
          }}
          min={minRange}
          max={maxRange}
          onAfterChange={nextValue => {
            onAfterChange();
            trackEvent(eventName, {
              previousValue,
              nextValue
            });
          }}
          onChange={(rangeValue: number) => {
            onChange(facetValue, rangeValue, 'slider');
            setSearchSource(SearchSource.Facet);
          }}
          step={step}
          trackStyle={{ backgroundColor: 'var(--cvna-color-blue-2)' }}
          value={sliderValue}
          onFocus={handleSliderFocus}
          onBlur={handleSliderBlur}
        />
        <div className="incline" />
      </RangeWrapper>
      <RangeLabels>
        <span data-qa={`${facetValue}-range-min`}>
          ${formatAndDropDecimal(minRange)}
        </span>
        <span data-qa={`${facetValue}-range-max`}>
          ${formatAndDropDecimal(maxRange)}
        </span>
      </RangeLabels>
    </Wrapper>
  );
}
